import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private readonly preferencesSubject: BehaviorSubject<UserPreferences>;
  public preferences$: Observable<UserPreferences>;

  constructor() {
    const stored = this.loadFromStorage();
    this.preferencesSubject = new BehaviorSubject<UserPreferences>(stored);
    this.preferences$ = this.preferencesSubject.asObservable();
  }

  private loadFromStorage(): UserPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
    return { ...DEFAULT_PREFERENCES };
  }

  private saveToStorage(preferences: UserPreferences): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  }

  // Get current preferences synchronously
  getPreferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  // Update preferences (partial update)
  updatePreferences(updates: Partial<UserPreferences>): void {
    const current = this.preferencesSubject.value;
    const updated = { ...current, ...updates };
    this.preferencesSubject.next(updated);
    this.saveToStorage(updated);
  }

  // Convenience method for page size
  getPageSize(): number {
    return this.preferencesSubject.value.pageSize;
  }

  // Observable for page size specifically
  getPageSize$(): Observable<number> {
    return this.preferences$.pipe(map(prefs => prefs.pageSize));
  }

  setPageSize(pageSize: number): void {
    this.updatePreferences({ pageSize });
  }

  // Reset to defaults
  resetPreferences(): void {
    this.preferencesSubject.next({ ...DEFAULT_PREFERENCES });
    this.saveToStorage(DEFAULT_PREFERENCES);
  }
}

export interface UserPreferences {
  pageSize: number;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  pageSize: 25,
};

const STORAGE_KEY = 'userPreferences';
